import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface OidcTokenResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
}

const SK_ACCESS_TOKEN  = 'rhsso_access_token';
const SK_REFRESH_TOKEN = 'rhsso_refresh_token';
const SK_ID_TOKEN      = 'rhsso_id_token';
const SK_EXPIRY        = 'rhsso_token_expiry';
const SK_CODE_VERIFIER = 'rhsso_pkce_code_verifier';
const SK_NONCE         = 'rhsso_oidc_nonce';
const SK_STATE         = 'rhsso_oidc_state';

/**
 * Camada de abstração de autenticação de usuário via RHSSO.
 * Implementa o fluxo Authorization Code + PKCE (RFC 7636), seguindo o padrão
 * do Authentication.php de referência da plataforma.
 *
 * Fluxo:
 *   1. login()          → gera PKCE, armazena na sessão, redireciona ao RHSSO
 *   2. handleCallback() → troca código de autorização por tokens
 *   3. getAccessToken() → retorna o token armazenado para uso no interceptor (com refresh automático se expirado)
 *   4. refresh()        → renova silenciosamente usando o refresh_token
 *   5. logout()         → invalida sessão local e redireciona ao endpoint de logout
 *
 * CORS: O realm do RHSSO deve permitir requisições cross-origin de localhost:4200
 * (dev) e dos domínios publicados (hom/prod) para o endpoint de token.
 */
@Injectable({ providedIn: 'root' })
export class RhssoAuthService {
  private readonly http = inject(HttpClient);
  private refreshInFlight: Promise<boolean> | null = null;

  // ─── Pública ──────────────────────────────────────────────────────────────

  /** Retorna true se há token de acesso válido na sessão. */
  isAuthenticated(): boolean {
    const token = sessionStorage.getItem(SK_ACCESS_TOKEN);
    if (!token) return false;

    const expiryMs = Number(sessionStorage.getItem(SK_EXPIRY));
    if (!expiryMs) return false;

    return Date.now() < expiryMs;
  }

  /**
   * Retorna o access_token atual ou null se não autenticado.
   * Se o token estiver expirado mas houver refresh_token, tenta renovar automaticamente.
   */
  async getAccessToken(): Promise<string | null> {
    const token = sessionStorage.getItem(SK_ACCESS_TOKEN);
    if (!token) return null;

    // Compara com a expiração calculada em storeTokens() a partir do
    // expires_in (relativo ao relógio do cliente), não com o 'exp' absoluto
    // do JWT — evita falso-positivo de expiração por desalinhamento de
    // relógio entre o cliente e o servidor RHSSO.
    const expiryMs = Number(sessionStorage.getItem(SK_EXPIRY));
    if (!expiryMs) return null;

    if (Date.now() < expiryMs) {
      return token;
    }

    // Token expirado, tenta refresh
    const refreshed = await this.refresh();

    if (refreshed) {
      return sessionStorage.getItem(SK_ACCESS_TOKEN);
    }

    return null;
  }

  /**
   * Decodifica as claims do access_token e retorna o nome de exibição do usuário.
   * Tenta, em ordem: name → preferred_username → sub.
   */
  getUserDisplayName(): string {
    const token = sessionStorage.getItem(SK_ACCESS_TOKEN);
    if (!token) return '';
    const payload = this.decodeTokenPayload(token);
    
    // Tenta name, preferred_username, sub - apenas se forem strings
    let displayName = '';
    if (typeof payload?.['name'] === 'string') {
      displayName = payload['name'];
    } else if (typeof payload?.['preferred_username'] === 'string') {
      displayName = payload['preferred_username'];
    } else if (typeof payload?.['sub'] === 'string') {
      displayName = payload['sub'];
    }
    
    return displayName.toUpperCase();
  }

  /** Returns the user profile claim from the access token (e.g. 'ADMINISTRATOR'). */
  getUserProfile(): string {
    const token = sessionStorage.getItem(SK_ACCESS_TOKEN);
    if (!token) return '';
    const payload = this.decodeTokenPayload(token);
    const profile = payload?.['profile'] ?? '';
    return typeof profile === 'string' ? profile : '';
  }

  /**
   * Inicia o fluxo OIDC Authorization Code com PKCE.
   * Gera code_verifier, code_challenge S256, state e nonce, armazena-os na
   * sessão e redireciona o navegador para o endpoint de autorização do RHSSO.
   * Equivalente ao método index() do Authentication.php de referência.
   */
  async login(): Promise<void> {
    const codeVerifier  = await this.generateCodeVerifier();
    const codeChallenge = await this.codeChallengeS256(codeVerifier);
    const state         = await this.randomUrlSafe(24);
    const nonce         = await this.randomUrlSafe(24);

    sessionStorage.setItem(SK_CODE_VERIFIER, codeVerifier);
    sessionStorage.setItem(SK_NONCE, nonce);
    sessionStorage.setItem(SK_STATE, state);

    const params = new URLSearchParams({
      client_id:             environment.iamClientId,
      response_type:         'code',
      scope:                 'openid',
      redirect_uri:          environment.iamRedirectUri,
      state,
      nonce,
      code_challenge:        codeChallenge,
      code_challenge_method: 'S256'
    });

    globalThis.location.href = `${environment.iamLoginUrl}?${params.toString()}`;
  }

  /**
   * Processa o callback OIDC: valida o state e troca o código pelo par de tokens.
   * Deve ser chamado pelo AuthCallbackComponent (rota /iam/login).
   * Retorna:
   *   'ok'    → autenticado com sucesso
   *   'error' → falha técnica (state inválido, erro HTTP, etc.)
   * 
   * Nota: Controle de acesso agora é feito via ROLES, não mais pelo campo 'profile'.
   */
  async handleCallback(code: string, returnedState: string): Promise<'ok' | 'unauthorized' | 'error'> {
    const storedState  = sessionStorage.getItem(SK_STATE);
    const codeVerifier = sessionStorage.getItem(SK_CODE_VERIFIER);

    if (!storedState || storedState !== returnedState || !codeVerifier) {
      return 'error';
    }

    const body = new HttpParams()
      .set('grant_type',    'authorization_code')
      .set('client_id',     environment.iamClientId)
      .set('code',          code)
      .set('redirect_uri',  environment.iamRedirectUri)
      .set('code_verifier', codeVerifier);

    try {
      const resp = await firstValueFrom(
        this.http.post<OidcTokenResponse>(this.tokenEndpoint(), body.toString(), {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        })
      );

      // Armazena tokens
      this.storeTokens(resp);
      this.clearPkceState();

      // Verifica se o usuário possui pelo menos uma role de VIEW
      const payload = this.decodeTokenPayload(resp.access_token);
      const roles = this.extractRoles(payload);
      const hasViewRole = roles.some(role => 
        role.startsWith('PROTHEUS_INTEGRATION_MONITOR_') && role.includes('_VIEW')
      );

      if (!hasViewRole) {
        this.clearSession(); // Limpa tokens da sessão
        return 'unauthorized';
      }

      return 'ok';
    } catch {
      return 'error';
    }
  }

  /**
   * Tenta renovar o access_token com o refresh_token armazenado.
   * Retorna true se a renovação foi bem-sucedida.
   *
   * Deduplica chamadas concorrentes: se várias requisições expirarem ao mesmo
   * tempo, todas compartilham a mesma tentativa de refresh em vez de disparar
   * chamadas paralelas com o mesmo refresh_token (o que, com rotação/reuso
   * único de refresh token no RHSSO, faz as chamadas "perdedoras" da corrida
   * receberem invalid_grant e derrubarem uma sessão que acabou de ser renovada
   * com sucesso por outra chamada concorrente).
   */
  async refresh(): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = sessionStorage.getItem(SK_REFRESH_TOKEN);
    if (!refreshToken) return false;

    const body = new HttpParams()
      .set('grant_type',    'refresh_token')
      .set('client_id',     environment.iamClientId)
      .set('refresh_token', refreshToken)
      .set('redirect_uri',  environment.iamRedirectUri);

    try {
      const resp = await firstValueFrom(
        this.http.post<OidcTokenResponse>(this.tokenEndpoint(), body.toString(), {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        })
      );
      this.storeTokens(resp);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Encerra a sessão localmente e redireciona ao endpoint de logout do RHSSO.
   * Equivalente ao método logout() do Authentication.php de referência.
   */
  logout(): void {
    const idToken      = sessionStorage.getItem(SK_ID_TOKEN);
    const postLogoutUri = environment.iamRedirectUri;
    this.clearSession();

    const params = new URLSearchParams({
      client_id:                environment.iamClientId,
      post_logout_redirect_uri: postLogoutUri
    });

    if (idToken) {
      params.set('id_token_hint', idToken);
    }

    globalThis.location.href = `${this.logoutEndpoint()}?${params.toString()}`;
  }

  /**
   * Indica se a URL é o próprio endpoint de token do RHSSO — usado pelo
   * interceptor para NÃO anexar Authorization/tentar refresh nessa chamada.
   * Compara com o endpoint real (via tokenEndpoint()) em vez de um match de
   * string como '/openid-connect/', porque em ambientes onde iamTokenUrl é
   * configurado (ex.: prod, atrás de proxy) o endpoint de token não segue
   * mais esse padrão — e tratar essa chamada como uma API protegida comum
   * gera uma recursão: o próprio refresh fica esperando um token que só
   * seria obtido por ele mesmo.
   */
  isTokenEndpoint(url: string): boolean {
    return url === this.tokenEndpoint();
  }

  // ─── Privado ──────────────────────────────────────────────────────────────

  /**
   * Deriva o endpoint de token a partir do endpoint de autorização configurado.
   * Se o environment define iamTokenUrl explicitamente, usa esse valor —
   * necessário em prod onde o redirect do browser usa a URL absoluta do RHSSO
   * mas as chamadas de token passam pelo proxy.
   * Padrão Keycloak: .../openid-connect/auth → .../openid-connect/token
   */
  private tokenEndpoint(): string {
    const env = environment as typeof environment & { iamTokenUrl?: string };
    return env.iamTokenUrl ?? environment.iamLoginUrl.replace('/openid-connect/auth', '/openid-connect/token');
  }

  /**
   * Deriva o endpoint de logout a partir do endpoint de autorização configurado.
   * Padrão Keycloak: .../openid-connect/auth → .../openid-connect/logout
   */
  private logoutEndpoint(): string {
    return environment.iamLoginUrl.replace('/openid-connect/auth', '/openid-connect/logout');
  }

  private storeTokens(resp: OidcTokenResponse): void {
    sessionStorage.setItem(SK_ACCESS_TOKEN,  resp.access_token);
    sessionStorage.setItem(SK_REFRESH_TOKEN, resp.refresh_token);

    // Usa expires_in (duração relativa, informada pelo próprio RHSSO) ancorado
    // no relógio do cliente, em vez do 'exp' absoluto do JWT. O 'exp' é definido
    // com base no relógio do servidor RHSSO; se o relógio do cliente estiver
    // dessincronizado (adiantado) em relação a ele, comparar Date.now() do
    // cliente contra um 'exp' absoluto do servidor pode fazer um token recém
    // emitido parecer já expirado. Ancorando no Date.now() do próprio cliente
    // para as duas pontas (aqui e na leitura em isAuthenticated/getAccessToken),
    // a duração concedida pelo RHSSO é respeitada sem depender do alinhamento
    // entre os dois relógios.
    const expiryMs = Date.now() + (resp.expires_in - 30) * 1000;
    sessionStorage.setItem(SK_EXPIRY, String(expiryMs));

    if (resp.id_token) {
      sessionStorage.setItem(SK_ID_TOKEN, resp.id_token);
    }
  }

  private clearPkceState(): void {
    [SK_CODE_VERIFIER, SK_NONCE, SK_STATE].forEach(k => sessionStorage.removeItem(k));
  }

  /**
   * Limpa todos os tokens e dados de sessão do sessionStorage.
   * Método público para permitir que o interceptor limpe a sessão quando necessário.
   */
  clearSession(): void {
    [SK_ACCESS_TOKEN, SK_REFRESH_TOKEN, SK_ID_TOKEN, SK_EXPIRY,
     SK_CODE_VERIFIER, SK_NONCE, SK_STATE].forEach(k => sessionStorage.removeItem(k));
  }

  // ─── Helpers criptográficos (equivalentes ao Authentication.php) ──────────

  /** Gera um code_verifier aleatório criptograficamente seguro (RFC 7636). */
  private async generateCodeVerifier(): Promise<string> {
    return this.base64url(crypto.getRandomValues(new Uint8Array(64)));
  }

  /** Calcula o code_challenge S256 do code_verifier (RFC 7636). */
  private async codeChallengeS256(verifier: string): Promise<string> {
    const data   = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64url(new Uint8Array(digest));
  }

  /** Gera bytes aleatórios seguros e retorna codificado em base64url. */
  private async randomUrlSafe(byteLength: number): Promise<string> {
    return this.base64url(crypto.getRandomValues(new Uint8Array(byteLength)));
  }

  /** Decodifica o payload de um JWT sem validar assinatura. Retorna null em caso de erro. */
  private decodeTokenPayload(token: string): Record<string, unknown> | null {
    try {
      const base64 = token.split('.')[1].replaceAll('-', '+').replaceAll('_', '/');
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  }

  /** Extrai roles do payload do token OIDC */
  private extractRoles(payload: Record<string, unknown> | null): string[] {
    if (!payload) return [];

    const realmAccess = payload['realm_access'] as { roles?: string[] } | undefined;
    const resourceAccess = payload['resource_access'] as Record<string, { roles?: string[] }> | undefined;
    const rolesArray = payload['roles'] as string[] | undefined;

    const roles: string[] = [];

    if (realmAccess?.roles) {
      roles.push(...realmAccess.roles);
    }

    if (resourceAccess) {
      Object.values(resourceAccess).forEach(resource => {
        if (resource.roles) {
          roles.push(...resource.roles);
        }
      });
    }

    if (rolesArray) {
      roles.push(...rolesArray);
    }

    return [...new Set(roles)];
  }

  /** Codifica bytes para base64url sem padding (RFC 4648 §5). */
  private base64url(bytes: Uint8Array): string {
    return btoa(String.fromCodePoint(...bytes))
      .replaceAll('+', '-')
      .replaceAll('/', '_')
      .replace(/={1,2}$/, '');
  }
}
